import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { BiAction } from '@core/services/logger/interfaces/bi.enums';
import { FLOW_RX_STEP_ATTR } from '@modules/rx-intuitive/models/flow-rx-step';
import { emptyGuid } from '@shared/models/consts';
import { PatientModelDto } from '@shared/models/rx-models/interfaces/patient-model-dto';
import { PopupService } from '@shared/services/popup.service';
import { PatientAppIframeId } from '@shared/models/enums/patient-app-iframe-id.enum';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { objectKeysToLowerCase } from '@shared/utils/object-keys-tolower-util';
import { Observable, fromEvent, Subject, BehaviorSubject } from 'rxjs';
import { audit, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { PatientAppEvent } from '../models/patient-app-event';
import { patientAppEventTypes } from '../models/patient-app-events.enum';
import { PatientStore } from '../state/patient-store';
import { PatientAppUrlBuilderService } from './patient-app-url-builder.service';
import { PatientAppPopUpComponent } from '@modules/patient/components/patient-app-popup/patient-app-popup.component';
import { MatLegacyDialogConfig as MatDialogConfig, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ShellStore } from '@shared/store/shell/shell-store';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { AuthInfoStore } from '@shared/store/authInfo/auth-info-store';
import { HostDispatcher } from '@shared/services/host-dispatcher';
import { Maybe } from '@/types/globals';

export enum PatientPopupCommands {
	EditPatient = 1,
	SearchPatient = 2
}
interface PatientToLowerDto {
	id?: number;
	uid: string;
	firstname: string;
	lastname: string;
	fullname?: string;
	chartnumber: string;
	dateofbirth: Date | string;
	gender?: number;
	mi?: string;
	zipcode: string;
	alignpatientid: string;
	type: number;
	lastscandate?: Date | string;
}

export interface SelectedPatientDto {
	id?: number;
	uid: string;
	firstName: string;
	lastName: string;
	fullName?: string;
	chartNumber: string;
	dateOfBirth: string;
	gender?: number;
	mi?: string;
	zipCode: string;
	alignPatientId: string;
	type: number;
	lastScanDate?: Date | string;
}

@Injectable({
	providedIn: 'root'
})
export class PatientAppIframeCommunicationService {
	private readonly moduleName = 'PatientAppIframeCommunicationService';

	private patientPopUpClosed$: Subject<boolean> = new Subject<boolean>();

	patientUpdatedEvent$: Subject<boolean> = new Subject<boolean>();
	patientPopupCommandEmitter$: Subject<PatientPopupCommands> = new Subject<PatientPopupCommands>();
	patientPopupCommandHandler$ = this.patientPopupCommandEmitter$.pipe(
		switchMap((command: PatientPopupCommands) => this.handlePatientPopupCommand(command)),
		audit(() => this.patientPopUpClosed$),
		tap(dialogRef => dialogRef.close())
	);

	private readonly patientSelectedEventSubject$: Subject<boolean> = new Subject<boolean>();
	patientSelectedEvent$ = this.patientSelectedEventSubject$.asObservable();

	private patientSavedEvent$: Subject<boolean> = new Subject<boolean>();

	private patientAppReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	isPatientAppReady$: Observable<boolean> = this.patientAppReady$.asObservable();

	private patientAppError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	isPatientAppError$: Observable<boolean> = this.patientAppError$.asObservable();

	private patientConflictPopupSubject = new Subject<PopUpActions>();
	patientConflictPopup$ = this.patientConflictPopupSubject.pipe();

	isListeningToPatientSave = false;
	private iFrameMessages$: Observable<MessageEvent> = fromEvent<MessageEvent>(window, 'message');
	private readonly inlinePatientAppIframeId = PatientAppIframeId.Inline;

	constructor(
		private logger: LoggerService,
		private biLogger: BiLoggerService,
		private patientStore: PatientStore,
		private shellStore: ShellStore,
		private shellQuery: ShellQuery,
		private authInfoStore: AuthInfoStore,
		private patientAppUrlBuilderService: PatientAppUrlBuilderService,
		private popupService: PopupService,
		private hostPlatformService: HostPlatformService,
		private hostDispatcher: HostDispatcher
	) {}

	registerToPatientAppEvents(): Observable<any> {
		this.logger.debug('registerToPatientApp', { module: this.moduleName });

		this.patientStore.update({ isPatientAppConfigured: true });

		return this.iFrameMessages$.pipe(
			withLatestFrom(this.shellQuery.patientAppUrl$),
			filter(([event, appSettings]) => this.isEventSourceIsPatientApp(event, appSettings)),
			tap(([event]) => this.handleEventFromPatientApp(event))
		);
	}

	savePatient(): Observable<boolean> {
		this.isListeningToPatientSave = true;
		this.postMessageForCreatePatient();

		return this.patientSavedEvent$.pipe(
			tap(() => {
				this.isListeningToPatientSave = false;
			})
		);
	}

	postMessageForCreatePatient(): void {
		this.postMessageToPatientApp(patientAppEventTypes.patientAppCreatePatient);
	}

	postMessageForClearPatient(): void {
		this.postMessageToPatientApp(patientAppEventTypes.patientAppClearPatient);
	}

	setPatientAppReadyState(isReady: boolean): void {
		this.patientAppReady$.next(isReady);
		this.patientAppError$.next(false);
	}

	getPatientPopupConfig(): Partial<MatDialogConfig> {
		const isHostPlatformScanner = this.hostPlatformService.isScanner;

		if (isHostPlatformScanner) {
			return { position: { top: '15px' } };
		}

		return {};
	}

	patientAppPopupClose(): void {
		this.popupService.closePopUp();
	}

	private handleEventFromPatientApp(event: MessageEvent): void {
		if (!event.data || !event.data.eventId) {
			this.logger.error(`No data or eventId from PatientApp to RxApp: ${JSON.stringify(event)}`, {
				module: this.moduleName
			});

			return;
		}

		const isPatientDataEmptyFromPopup = this.popupService.isPopupOpenById('patient-app-popup') && !event.data?.data;

		if (isPatientDataEmptyFromPopup) {
			this.logger.info('No patient data from popup - no update made.', {
				module: this.moduleName
			});
			this.patientPopUpClosed$.next(true);

			return;
		}

		const { eventId, data: eventData, status, iframeId } = event.data as PatientAppEvent;

		this.logger.info(`Event from PatientApp to RxApp: eventId ${eventId}, eventName ${patientAppEventTypes[eventId]}`, {
			module: this.moduleName
		});
		switch (eventId as patientAppEventTypes) {
			case patientAppEventTypes.patientAppReady:
				if (eventData === 'patientAppReady') {
					this.patientAppReady$.next(true);
				}
				break;
			case patientAppEventTypes.patientAppInit:
				const payload = {
					accessToken: this.authInfoStore.getValue().accessToken,
					selectedCompanyId: this.shellQuery.companyId,
					selectedDoctorId: this.shellQuery.doctor?.Id,
					sessionId: this.authInfoStore.getValue().sessionId,
					refreshTokenUrl: `${this.authInfoStore.getValue().authUrl}session/refresh?sessionId={0}&appId={1}`,
					...(iframeId === PatientAppIframeId.Edit ? { initialPatientData: this.getInitialPatientData() } : {})
				};

				this.postMessageToPatientApp(patientAppEventTypes.patientAppInit, payload, iframeId);
				break;
			case patientAppEventTypes.patientAppAddPatient:
				this.patientPopUpClosed$.next(true);
				break;
			case patientAppEventTypes.patientAppSelectedPatient:
				this.patientSelectedEventSubject$.next(true);
				this.onPatientSelected(eventData);
				this.patientPopUpClosed$.next(true);
				break;
			case patientAppEventTypes.patientAppUpdateHostPatient:
				this.onPatientUpdated(eventData);
				if (eventData != null) {
					this.patientUpdatedEvent$.next(true);
					this.patientPopUpClosed$.next(true);
				}
				break;
			case patientAppEventTypes.patientAppSelectedPatientConflict:
				// TODO
				break;
			case patientAppEventTypes.patientAppRedirect:
				this.hostDispatcher.dispatchExpiredTokenEvent(iframeId || this.inlinePatientAppIframeId);
				this.patientPopUpClosed$.next(true);
				break;
			case patientAppEventTypes.patientAppDialog:
				if (eventData === 'patientAppDialogOpen') {
					this.shellStore.update({ isPatientAppDialogOpen: status === 'open' });
				}
				break;
			case patientAppEventTypes.patientAppSearchPatient:
				this.patientPopupCommandEmitter$.next(PatientPopupCommands.SearchPatient);
				break;
			case patientAppEventTypes.patientAppPatientInConflict:
				const isPatientInConflict = eventData;

				this.patientStore.update({ isPatientInConflict: !!isPatientInConflict });
				this.pushAfterSaveEvent(false);
				break;
			case patientAppEventTypes.patientAppError:
				this.patientAppError$.next(true);
				break;
			case patientAppEventTypes.patientAppConfirmConflictPopup:
				this.patientConflictPopupSubject.next(PopUpActions.Ok);
				break;
			case patientAppEventTypes.patientAppCancelConflictPopup:
				this.patientConflictPopupSubject.next(PopUpActions.Cancel);
				break;
			default:
				this.logger.error(`Unknown event from PatientApp to RxApp: ${JSON.stringify(event)}`, {
					module: this.moduleName
				});
		}
	}

	private getInitialPatientData(): object | null {
		const patient = this.patientStore.getValue().patient;

		if (!patient) {
			return null;
		}

		return {
			...patient,
			DateOfBirth: patient.DateOfBirth instanceof Date ? patient.DateOfBirth.toISOString() : patient.DateOfBirth
		};
	}

	private postMessageToPatientApp(eventId: patientAppEventTypes, data?: any, iframeId?: string): void {
		const message = { eventId, data };
		const iframe: HTMLIFrameElement = document.getElementById(iframeId || this.inlinePatientAppIframeId) as HTMLIFrameElement;

		iframe?.contentWindow?.postMessage(message, '*');
	}

	private isEventSourceIsPatientApp(event: MessageEvent, patientAppUrl: Maybe<string>): boolean {
		return patientAppUrl?.includes(event.origin) ?? false;
	}

	private onPatientUpdated(data: PatientModelDto): void {
		if (data === null) {
			this.patientStore.update({ patient: null, isPatientCleared: true });

			return;
		}

		const patientToUpdate = this.mapPatientAppModelToRxModel(data);

		this.patientStore.updatePatient(patientToUpdate, null, null);
		this.pushAfterSaveEvent(true);
		this.trackPatientEvent(BiAction.Update, '');
	}

	private onPatientSelected(data: any): void {
		const patient = data as SelectedPatientDto;

		if (!patient) {
			this.patientStore.update({ patient: null });

			return;
		}

		const patientToUpdate: PatientModelDto = {
			AlignPatientId: patient.alignPatientId,
			ChartNumber: patient.chartNumber,
			DateOfBirth: patient.dateOfBirth,
			FirstName: patient.firstName,
			FullName: patient.fullName,
			Gender: patient.gender,
			Id: patient.id,
			LastName: patient.lastName,
			LastScanDate: patient.lastScanDate,
			MI: patient.mi,
			Type: patient.type,
			UID: patient.uid,
			ZipCode: patient.zipCode
		};

		const hadPatient = !!this.patientStore.getValue().patient?.UID;

		this.patientStore.updatePatient(patientToUpdate, this.shellQuery.dateFormat, this.shellQuery.regulatoryDOBMask);
		this.pushAfterSaveEvent(true);
		this.trackPatientEvent(BiAction.Select, hadPatient ? 'patient-switched' : 'initial');
	}

	private pushAfterSaveEvent(isPatientSaved: boolean): void {
		if (!this.isListeningToPatientSave) {
			return;
		}
		this.patientSavedEvent$.next(isPatientSaved);
		this.isListeningToPatientSave = false;
	}

	private mapPatientAppModelToRxModel(data: PatientModelDto): PatientModelDto {
		const patientToLower = objectKeysToLowerCase(data) as PatientToLowerDto;

		return {
			Id: patientToLower.id,
			UID: patientToLower.uid || emptyGuid,
			FirstName: patientToLower.firstname,
			LastName: patientToLower.lastname,
			ChartNumber: patientToLower.chartnumber,
			ZipCode: patientToLower.zipcode,
			DateOfBirth: patientToLower.dateofbirth?.toString(),
			MI: patientToLower.mi,
			Gender: patientToLower.gender,
			Type: patientToLower.type || 1,
			AlignPatientId: patientToLower.alignpatientid,
			FullName: `${patientToLower.firstname} ${patientToLower.lastname}`
		};
	}

	private handlePatientPopupCommand(command: PatientPopupCommands): Observable<MatDialogRef<PatientAppPopUpComponent>> {
		switch (command) {
			case PatientPopupCommands.EditPatient:
				return this.openEditPopup();
			case PatientPopupCommands.SearchPatient:
				return this.openSearchPopup();
		}
	}

	private openEditPopup(): Observable<MatDialogRef<PatientAppPopUpComponent>> {
		const config: Partial<MatDialogConfig> = this.getPatientPopupConfig();

		return this.patientAppUrlBuilderService
			.getEditPatientAppUrl(false)
			.pipe(map(url => this.popupService.openPatientAppPopUp({ editPatientAppUrl: url }, config)));
	}

	private openSearchPopup(): Observable<MatDialogRef<PatientAppPopUpComponent>> {
		const config: Partial<MatDialogConfig> = this.getPatientPopupConfig();

		return this.patientAppUrlBuilderService
			.getSearchPatientAppUrl()
			.pipe(map(url => this.popupService.openPatientAppPopUp({ searchPatientAppUrl: url }, config)));
	}

	private trackPatientEvent(action: string, selectedValue: string): void {
		const step = this.shellQuery.flowRxStep;

		if (!step) {
			return;
		}

		void this.biLogger.reporter?.trackUserAction({
			objectName: 'patient-selector',
			action,
			selectedValue,
			customAttributes: { [FLOW_RX_STEP_ATTR]: step }
		});
	}
}
